import { Link } from "react-router-dom";
import { news } from "@/mocks/news";
import { channels } from "@/mocks/categories";
import { SectionHeading } from "@/components/feature/UI";
import { StandardCard, ListRow } from "@/components/feature/NewsCard";
import { channelFill, channelIcon } from "@/utils/channel";
import { formatNum } from "@/hooks/useSeo";

export function ChannelMatrix() {
  return (
    <section className="bg-background-100/70">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
        <SectionHeading
          title="五大栏目 · 一个都不放过"
          desc="按吃瓜人群细分，想找哪种新瓜，直接点进对应栏目慢慢扒。"
          more={{ to: "/channels", label: "进入全部资讯" }}
        />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {channels.map((c) => {
            const count = news.filter((n) => n.category === c.key).length;
            return (
              <Link
                key={c.key}
                to={`/channels?category=${c.key}`}
                className="group relative overflow-hidden rounded-lg"
              >
                <div className="relative h-40 w-full overflow-hidden md:h-48">
                  <img
                    src={c.cover}
                    alt={c.name}
                    title={`${c.name}栏目`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-foreground-950/10"></div>
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <span className={`mb-2 inline-flex h-8 w-8 items-center justify-center rounded-md ${channelFill(c.key)} text-background-50`}>
                      <i className={channelIcon(c.key)}></i>
                    </span>
                    <h3 className="font-display text-base font-bold text-background-50">{c.name}</h3>
                    <p className="mt-0.5 text-xs text-background-100/70">
                      {count} 篇速递 · {c.tagline}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Trending() {
  const feed = [...news].slice(0, 6);
  const hot = [...news].sort((a, b) => b.reads - a.reads).slice(0, 6);
  return (
    <section className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 px-4 py-12 md:px-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <SectionHeading title="编辑部精选速递" en="PICK OF THE DAY" more={{ to: "/channels", label: "查看全部" }} />
        <div className="flex flex-col gap-4">
          {feed.map((n, i) => (
            <ListRow key={n.id} item={n} index={i} />
          ))}
        </div>
      </div>
      <aside className="flex flex-col gap-6">
        <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-fire-fill text-sm"></i>
              </span>
              热度飙升榜
            </h3>
            <span className="text-xs text-foreground-400">实时</span>
          </div>
          <ol className="space-y-3">
            {hot.map((n, i) => (
              <li key={n.id}>
                <Link to={`/news/${n.id}`} className="group flex items-start gap-3">
                  <span
                    className={`font-display mt-0.5 w-6 shrink-0 text-center text-xl font-black ${
                      i < 3 ? "text-primary-500" : "text-foreground-300"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className="flex-1">
                    <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                      {n.title}
                    </span>
                    <span className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
                      <span className="text-primary-500">{formatNum(n.reads)} 阅读</span>
                      <span>{n.categoryName}</span>
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </div>

        <div className="relative overflow-hidden rounded-lg">
          <div className="relative h-56 w-full">
            <img
              src="https://readdy.ai/api/search-image?query=editorial%20photography%20of%20an%20open%20vintage%20newspaper%20with%20bold%20headlines%20on%20a%20dark%20desk%2C%20warm%20desk%20lamp%20glow%20and%20scattered%20paper%2C%20amber%20and%20deep%20red%20tones%2C%20moody%20cinematic%2C%20high%20detail%20magazine%20style&width=900&height=700&seq=hotcard-bg&orientation=portrait"
              alt="吃瓜与写作"
              title="每日新瓜编辑手记"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-foreground-950/80"></div>
            <div className="absolute inset-0 flex flex-col justify-between p-5">
              <span className="inline-flex w-max items-center gap-1 rounded-md bg-accent-500 px-2 py-1 text-xs font-medium text-foreground-950">
                <i className="ri-pen-nib-line"></i>
                编辑手记
              </span>
              <div>
                <h4 className="font-display text-lg font-bold leading-snug text-background-50">
                  为什么我们坚持「理性吃瓜」？
                </h4>
                <p className="mt-2 text-sm leading-6 text-background-100/80">
                  流量会过去，真相会被埋没。与其跟风起哄，不如让每一篇盘点都经得起复盘。
                </p>
                <Link
                  to="/about"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent-300 transition-colors hover:text-accent-200"
                >
                  了解我们的立场
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </section>
  );
}

export function CategoryZones() {
  const zones = ["wanghong", "lianxisheng", "yanyuan"];
  return (
    <section className="bg-background-100/70">
      <div className="mx-auto flex max-w-[1200px] flex-col gap-10 px-4 py-12 md:px-6">
        {zones.map((key) => {
          const list = news.filter((n) => n.category === key).slice(0, 3);
          const channel = channels.find((c) => c.key === key);
          if (!channel || list.length === 0) return null;
          return (
            <div key={key}>
              <SectionHeading
                title={channel.name}
                desc={channel.desc}
                more={{ to: `/channels?category=${key}`, label: "进入栏目" }}
              />
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((n) => (
                  <StandardCard key={n.id} item={n} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function SurenBanner() {
  const suren = news.filter((n) => n.category === "suren");
  const top = suren[0];
  if (!top) return null;
  return (
    <section className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
      <div className="relative overflow-hidden rounded-lg">
        <div className="relative h-[340px] w-full md:h-[400px]">
          <img
            src={top.cover}
            alt="素人变网红黑幕专题"
            title="素人变网红黑幕专题"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-transparent"></div>
        </div>
        <div className="absolute inset-0 flex flex-col justify-center p-6 md:p-12 lg:max-w-[55%]">
          <span className="mb-4 inline-flex w-max items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1 text-xs font-semibold text-foreground-950">
            <i className="ri-user-heart-line"></i>
            素人变网红黑幕 · 深度专题
          </span>
          <h2 className="font-display text-2xl font-black leading-tight text-background-50 md:text-4xl">
            你以为的「一夜爆红」
            <br />
            其实是一条精密流水线
          </h2>
          <p className="mt-3 max-w-md text-sm leading-6 text-background-100/85 md:text-base">
            拆解脚本、投放与造星工厂的运作逻辑，看看偶然爆红背后，究竟藏着多少被精心设计的必然。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/news/${top.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line"></i>
              阅读本期专题
            </Link>
            <Link
              to="/channels?category=suren"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              进入素人栏目
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}